import * as React from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Lock, Pencil, Printer, Send, Undo2 } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { formatDate, formatMetric, trendOf } from '@/lib/format'
import { categoryMovement, frozenSnapshot, monthLabel } from '@/lib/report'
import type { Report, ReportEvaluation, ReportMetric, ReportSection, ReportSnapshot } from '@/lib/types'
import { ReportSectionBlocks } from '@/components/report-sections'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ErrorNote, Loading, Markdown } from '@/components/page'
import { ScaleLegend, scaleLabelFor } from '@/components/scale-legend'
import { RadarChart } from '@/components/radar-chart'
import { cn } from '@/lib/utils'
import { useDirty, useDiscardGuard } from '@/hooks/use-unsaved'
import { DiscardPrompt } from '@/components/discard-prompt'

/**
 * One monthly report, rendered as a document rather than a dashboard — it exists
 * to be read start to finish, and printed to PDF for a partner conversation.
 *
 * Where the numbers come from depends on status, and that difference is the whole
 * point of the feature:
 *
 *   published — `reports.snapshot`, frozen by the trigger at publish time. A
 *               reading corrected months later does not rewrite what the lead saw.
 *   draft     — the `report_preview` RPC, which runs the SAME SQL function live so
 *               the coach can watch the month fill in. Coaches only; a lead never
 *               sees a draft (migrations/0018_reports.sql).
 */
export function ReportDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { isCoach } = useAuth()
  const [editing, setEditing] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  const [actionError, setActionError] = React.useState<string | null>(null)

  const { data, loading, error, refetch } = useQuery(async () => {
    if (!id) return null
    const { data: row, error: err } = await sb().from('reports').select('*').eq('id', id).maybeSingle()
    if (err) throw err
    if (!row) return null

    const report = row as Report

    // Sections are authored, never generated, so they're read live in both
    // states — a published report's prose stays correctable (0018), and these
    // are prose.
    const { data: secs, error: serr } = await sb()
      .from('report_sections')
      .select('*')
      .eq('report_id', report.id)
      .order('placement')
      .order('display_order')
    if (serr) throw serr
    const sections = (secs ?? []) as ReportSection[]

    const frozen = frozenSnapshot(report.snapshot)
    if (frozen) return { report, sections, snapshot: frozen, live: false }

    const { data: preview, error: perr } = await sb().rpc('report_preview', {
      team: report.team_id,
      month: report.period_month,
    })
    if (perr) throw perr
    return { report, sections, snapshot: preview as ReportSnapshot, live: true }
  }, [id])

  async function setStatus(status: Report['status']) {
    if (!data) return
    if (status === 'published') {
      if (
        !window.confirm(
          `Publish this report to the team lead?\n\nThe metric readings and evaluation scores shown here are frozen as they are now — later corrections won't change this document. You can still fix the wording.`,
        )
      )
        return
    } else if (
      !window.confirm(
        'Return this report to draft?\n\nThe team lead loses access to it, and the frozen numbers are discarded — publishing again re-reads them as they stand then.',
      )
    ) {
      return
    }

    setBusy(true)
    setActionError(null)
    const { error: err } = await sb().from('reports').update({ status }).eq('id', data.report.id)
    setBusy(false)
    if (err) return setActionError(err.message)
    await refetch()
  }

  if (loading && !data) return <Loading />
  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <ErrorNote error={error} />
      </div>
    )
  }
  if (!data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <p className="text-sm text-muted-foreground">
          That report doesn&apos;t exist, or it hasn&apos;t been published to you yet.
        </p>
        <Button asChild variant="link" className="mt-2">
          <Link to="/reports">Back to reports</Link>
        </Button>
      </div>
    )
  }

  const { report, sections, snapshot, live } = data

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6 lg:py-8">
      {/* Chrome. `no-print` keeps every control out of the PDF — see index.css. */}
      <div className="no-print mb-5 flex flex-wrap items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/reports">
            <ArrowLeft /> Reports
          </Link>
        </Button>
        <Badge variant={report.status === 'published' ? 'success' : 'muted'} className="text-[10px]">
          {report.status}
        </Badge>
        {report.status === 'draft' && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" /> only Don&apos;t Panic Labs can see this
          </span>
        )}
        <div className="flex-1" />
        <Button variant="ghost" size="sm" onClick={() => window.print()}>
          <Printer /> Print / PDF
        </Button>
        {isCoach && !editing && (
          <>
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
              <Pencil /> Edit
            </Button>
            {report.status === 'draft' ? (
              <Button size="sm" disabled={busy} onClick={() => void setStatus('published')}>
                <Send /> Publish
              </Button>
            ) : (
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => void setStatus('draft')}>
                <Undo2 /> Return to draft
              </Button>
            )}
          </>
        )}
      </div>

      {actionError && (
        <div className="no-print mb-4">
          <ErrorNote error={actionError} />
        </div>
      )}

      {live && isCoach && (
        <p className="no-print mb-4 rounded-md border border-dashed bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          Draft — the numbers below are live and will keep moving. They freeze when you publish.
        </p>
      )}

      {editing ? (
        <ReportEditor
          report={report}
          onCancel={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false)
            await refetch()
          }}
        />
      ) : (
        <ReportDocument
          report={report}
          sections={sections}
          snapshot={snapshot}
          canEdit={isCoach}
          onSectionsChanged={refetch}
        />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* The document                                                               */
/* -------------------------------------------------------------------------- */

function ReportDocument({
  report,
  sections,
  snapshot,
  canEdit,
  onSectionsChanged,
}: {
  report: Report
  sections: ReportSection[]
  snapshot: ReportSnapshot
  canEdit: boolean
  onSectionsChanged: () => Promise<void>
}) {
  const evaluations = snapshot.evaluations ?? []
  return (
    <article className="report-sheet rounded-lg border bg-card p-6 shadow-sm sm:p-10">
      <header className="border-b pb-5">
        <h1 className="text-3xl font-bold leading-tight tracking-tight text-primary">
          {snapshot.team?.company || snapshot.team?.name}
        </h1>
        <p className="mt-1 text-lg font-medium leading-snug text-primary/85">{report.title}</p>
        <p className="mt-1.5 text-xs text-muted-foreground">
          {monthLabel(report.period_month)}
          {report.subtitle ? ` · ${report.subtitle}` : ''}
        </p>
      </header>

      <Section title="Executive Summary">
        <Prose text={report.intro} placeholder="No introduction written yet." />
      </Section>

      <ReportSectionBlocks
        reportId={report.id}
        placement="before_data"
        sections={sections}
        canEdit={canEdit}
        onChanged={onSectionsChanged}
      />

      <Section
        title="Metrics"
        note={
          snapshot.metrics.length > 0
            ? `Readings recorded during ${monthLabel(report.period_month)}. Change is measured against the last reading before the month.`
            : undefined
        }
      >
        {snapshot.metrics.length === 0 ? (
          <Empty>No metric readings were recorded in {monthLabel(report.period_month)}.</Empty>
        ) : (
          <div className="divide-y">
            {snapshot.metrics.map((m) => (
              <MetricRow key={m.id} metric={m} />
            ))}
          </div>
        )}
        <Commentary text={report.metrics_note} />
      </Section>

      <Section
        title="Team Competency Assessment"
        note={
          evaluations.length === 0
            ? undefined
            : snapshot.evaluation_in_period
              ? 'Assessed this month.'
              : 'No assessment was run this month; this is where the team stood at month end.'
        }
      >
        {evaluations.length === 0 ? (
          <Empty>This team hasn&apos;t been evaluated yet.</Empty>
        ) : (
          <div className="space-y-8">
            {evaluations.map((e, i) => (
              <EvaluationBlock
                key={e.id}
                evaluation={e}
                // Only the most recent run gets the comparison: it's the prior
                // run of the same template, which is the one before THIS one.
                comparison={i === 0 ? snapshot.comparison : null}
              />
            ))}
          </div>
        )}
        <Commentary text={report.evaluation_note} />
      </Section>

      <ReportSectionBlocks
        reportId={report.id}
        placement="after_data"
        sections={sections}
        canEdit={canEdit}
        onChanged={onSectionsChanged}
      />

      <Section title="Conclusion">
        <Prose text={report.conclusion} placeholder="No conclusion written yet." />
      </Section>

      <footer className="mt-8 border-t pt-3 text-[11px] text-muted-foreground">
        {report.status === 'published' && report.published_at
          ? `Published ${formatDate(report.published_at)} · figures as of that date`
          : 'Draft · figures are live and not yet frozen'}
        {' · Don’t Panic Labs Team Accelerator'}
      </footer>
    </article>
  )
}

function Section({
  title,
  note,
  children,
}: {
  title: string
  note?: string
  children: React.ReactNode
}) {
  return (
    <section className="report-section mt-8">
      <h2 className="border-b pb-1.5 text-lg font-semibold text-primary">{title}</h2>
      {note && <p className="mt-2 text-xs text-muted-foreground">{note}</p>}
      <div className="mt-3">{children}</div>
    </section>
  )
}

function Prose({ text, placeholder }: { text: string | null; placeholder: string }) {
  if (!text?.trim()) return <Empty>{placeholder}</Empty>
  return <Markdown source={text} />
}

/**
 * The coach's reading of a generated block, under the numbers it's about.
 * Renders nothing at all when empty — an absent commentary is not a gap in the
 * document the way an absent introduction is, so it gets no placeholder.
 */
function Commentary({ text }: { text: string | null }) {
  if (!text?.trim()) return null
  return (
    <div className="mt-5 border-t pt-4">
      <Markdown source={text} />
    </div>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm italic text-muted-foreground">{children}</p>
}

/* -------------------------------------------------------------------------- */
/* Metrics                                                                    */
/* -------------------------------------------------------------------------- */

function MetricRow({ metric }: { metric: ReportMetric }) {
  const trend =
    metric.current != null
      ? trendOf(metric, metric.current, metric.previous ?? undefined)
      : null
  const history = metric.history ?? []

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-6 gap-y-2 py-3 sm:grid-cols-[minmax(0,1fr)_7rem_9rem]">
      <div className="min-w-0">
        <p className="text-sm font-medium">{metric.label}</p>
        {metric.description && (
          <p className="mt-0.5 text-xs text-muted-foreground">{metric.description}</p>
        )}
        {/* The readings ARE the table view for the sparkline beside them, which
            is why they're spelled out rather than left to the shape of a line. */}
        <p className="mt-1 text-xs tabular-nums text-muted-foreground">
          {metric.readings.map((r) => `${r.period_label} ${formatMetric(r.value, metric.unit)}`).join(' · ')}
          {metric.target != null && ` · target ${formatMetric(metric.target, metric.unit)}`}
        </p>
      </div>

      <div className="text-right sm:order-2">
        <div className="text-xl font-semibold tabular-nums leading-none">
          {metric.current != null ? formatMetric(metric.current, metric.unit) : '—'}
        </div>
        {trend && (
          <div
            className={cn(
              'mt-1 text-xs font-medium tabular-nums',
              trend.delta === 0
                ? 'text-muted-foreground'
                : trend.better
                  ? 'text-better'
                  : 'text-worse',
            )}
          >
            {trend.delta === 0
              ? 'no change'
              : `${trend.delta > 0 ? '+' : ''}${formatMetric(trend.delta, metric.unit)}`}
          </div>
        )}
        {!trend && metric.current != null && (
          <div className="mt-1 text-xs text-muted-foreground">first reading</div>
        )}
      </div>

      <div className="col-span-2 sm:order-3 sm:col-span-1">
        <Sparkline
          points={history.map((h) => h.value)}
          label={`${metric.label}: ${history.map((h) => `${h.period_label} ${h.value}`).join(', ')}`}
        />
      </div>
    </div>
  )
}

/**
 * Trailing readings as one line. Fixed size rather than a measured container: a
 * document gets printed, and a chart that waits for a layout measurement can
 * come out of the print pipeline empty.
 *
 * One series, so no legend — the row's own label names it (dataviz skill), and
 * the readings are written out beside it for anyone who can't read the shape.
 */
function Sparkline({ points, label }: { points: number[]; label: string }) {
  if (points.length < 2) return null
  const W = 96
  const H = 28
  const pad = 3
  const min = Math.min(...points)
  const max = Math.max(...points)
  const span = max - min || 1
  const x = (i: number) => pad + (i / (points.length - 1)) * (W - pad * 2)
  const y = (v: number) => H - pad - ((v - min) / span) * (H - pad * 2)
  const d = points.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ')

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      className="h-7 w-24"
      role="img"
      aria-label={label}
    >
      <path
        d={d}
        fill="none"
        stroke="var(--chart-line)"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Terminal point, ringed in the surface color so it stays legible where
          the line doubles back on itself. */}
      <circle
        cx={x(points.length - 1)}
        cy={y(points[points.length - 1])}
        r={2.5}
        fill="var(--chart-line)"
        stroke="hsl(var(--card))"
        strokeWidth={1.5}
      />
    </svg>
  )
}

/* -------------------------------------------------------------------------- */
/* Evaluations                                                                */
/* -------------------------------------------------------------------------- */

function EvaluationBlock({
  evaluation,
  comparison,
}: {
  evaluation: ReportEvaluation
  comparison: ReportEvaluation | null
}) {
  const template = evaluation.template
  const rows = categoryMovement(evaluation, comparison)
  const min = template.scale_min
  const max = template.scale_max
  const pos = (v: number) => ((v - min) / (max - min)) * 100
  const scaleName = scaleLabelFor(template, evaluation.average)

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <p className="text-sm font-medium">{evaluation.name}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {template.name}
            {template.version ? ` ${template.version}` : ''} · conducted{' '}
            {formatDate(evaluation.conducted_on)}
            {evaluation.facilitator ? ` · facilitated by ${evaluation.facilitator}` : ''} ·{' '}
            {evaluation.scored}/{evaluation.total} scored
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-semibold tabular-nums leading-none">
            {evaluation.average != null ? evaluation.average.toFixed(1) : '—'}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {scaleName ?? `avg of ${max}`}
          </div>
        </div>
      </div>

      <div className="mt-3">
        <ScaleLegend template={template} active={evaluation.average} />
      </div>

      {/* Two series share the plot, so identity is never carried by color alone. */}
      {comparison && (
        <div className="mt-3 flex flex-wrap items-center gap-4 text-xs">
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: 'var(--chart-before)' }}
            />
            {comparison.name}{' '}
            <span className="text-muted-foreground">({formatDate(comparison.conducted_on)})</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: 'var(--chart-after)' }}
            />
            {evaluation.name}
          </span>
        </div>
      )}

      {/* The whole assessment as one shape, then the per-area detail below it. A
          printed report is skimmed once — this is the picture someone looks at,
          and the rows underneath are what they check a number against. One legend
          above serves both, hence hideLegend. */}
      <RadarChart
        axes={rows.map((r) => ({ label: r.name, now: r.now, before: r.before }))}
        template={template}
        nowLabel={evaluation.name}
        beforeLabel={comparison?.name ?? null}
        hideLegend
        className="mx-auto mt-3 max-w-xl"
      />

      {/* Position on a track, not bar length: the scale starts at 1, and a bar
          from a non-zero baseline overstates every difference. Same encoding the
          Evaluations page uses, so the two read as one system. The legend above
          the radar covers these rows too — same two series, same two colors. */}
      <div className="mt-2 space-y-1">
        {rows.map((r) => (
          <div
            key={r.name}
            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 py-1.5 sm:grid-cols-[minmax(0,15rem)_minmax(0,1fr)_auto]"
          >
            {/* Wraps rather than truncates: this is a document, and "Software
                Engineering Manage…" is not a knowledge area anyone can act on. */}
            <div className="text-sm leading-snug sm:order-1">{r.name}</div>

            <div className="order-3 col-span-2 sm:order-2 sm:col-span-1">
              <div className="relative h-5">
                <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-muted" />
                {r.now == null ? (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                    not scored
                  </span>
                ) : (
                  <>
                    {r.before != null && (
                      <div
                        className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full"
                        style={{
                          left: `${Math.min(pos(r.before), pos(r.now))}%`,
                          width: `${Math.abs(pos(r.now) - pos(r.before))}%`,
                          background: 'var(--chart-before)',
                          opacity: 0.55,
                        }}
                      />
                    )}
                    {r.before != null && (
                      <Dot left={pos(r.before)} color="var(--chart-before)" label={`was ${r.before.toFixed(1)}`} />
                    )}
                    <Dot left={pos(r.now)} color="var(--chart-after)" label={`${r.now.toFixed(1)}`} />
                  </>
                )}
              </div>
            </div>

            <div className="order-2 flex items-baseline justify-end gap-2 sm:order-3">
              <span className="text-sm font-semibold tabular-nums">
                {r.now != null ? r.now.toFixed(1) : '—'}
              </span>
              {r.delta != null && Math.abs(r.delta) >= 0.05 && (
                <span
                  className={cn(
                    'text-xs font-medium tabular-nums',
                    r.delta > 0 ? 'text-better' : 'text-worse',
                  )}
                >
                  {r.delta > 0 ? '+' : ''}
                  {r.delta.toFixed(1)}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-2 flex justify-between border-t pt-1.5 text-[11px] text-muted-foreground">
        <span>{min}</span>
        <span>{max}</span>
      </div>

      {evaluation.summary && (
        <div className="mt-3 rounded-md bg-muted/40 p-3">
          <Markdown source={evaluation.summary} />
        </div>
      )}
    </div>
  )
}

function Dot({ left, color, label }: { left: number; color: string; label: string }) {
  return (
    <span
      className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-card"
      style={{ left: `${left}%`, background: color }}
      title={label}
      aria-label={label}
    />
  )
}

/* -------------------------------------------------------------------------- */
/* Editing                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Coach-only, and only the prose. Nothing here can touch the numbers: the
 * snapshot is written by app.reports_guard, and a published report's month is
 * pinned there too — so this form doesn't offer to change it.
 */
function ReportEditor({
  report,
  onCancel,
  onSaved,
}: {
  report: Report
  onCancel: () => void
  onSaved: () => Promise<void>
}) {
  const [title, setTitle] = React.useState(report.title)
  const [subtitle, setSubtitle] = React.useState(report.subtitle ?? '')
  const [intro, setIntro] = React.useState(report.intro ?? '')
  const [metricsNote, setMetricsNote] = React.useState(report.metrics_note ?? '')
  const [evaluationNote, setEvaluationNote] = React.useState(report.evaluation_note ?? '')
  const [conclusion, setConclusion] = React.useState(report.conclusion ?? '')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  // The longest writing anyone does in this portal, and it is not a dialog —
  // so the browser's unload prompt is the only thing standing between a
  // half-written month and a reflexive ⌘R.
  const dirty = useDirty({ title, subtitle, intro, metricsNote, evaluationNote, conclusion })
  const guard = useDiscardGuard(dirty, onCancel)

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) return setError('A report needs a title.')
    setBusy(true)
    setError(null)
    const { error: err } = await sb()
      .from('reports')
      .update({
        title: title.trim(),
        subtitle: subtitle.trim() || null,
        intro: intro.trim() || null,
        metrics_note: metricsNote.trim() || null,
        evaluation_note: evaluationNote.trim() || null,
        conclusion: conclusion.trim() || null,
      })
      .eq('id', report.id)
    setBusy(false)
    if (err) return setError(err.message)
    await onSaved()
  }

  return (
    <form onSubmit={save} className="space-y-5 rounded-lg border bg-card p-6 shadow-sm">
      <DiscardPrompt guard={guard} what="this report" />
      <div>
        <p className="text-sm font-medium">
          Editing the {monthLabel(report.period_month)} report
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          The metrics and assessment blocks are composed from the month&apos;s data; everything
          here is yours, including the reading of those two. Anything longer belongs in a
          section, which you add on the document itself. Markdown works:{' '}
          <code className="text-[0.85em]">**bold**</code>, lists, headings.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="etitle">Title</Label>
          <Input id="etitle" value={title} onChange={(e) => setTitle(e.target.value)} required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="esub">Subtitle</Label>
          <Input
            id="esub"
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            placeholder="Optional"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="eintro">Executive summary</Label>
        <Textarea
          id="eintro"
          value={intro}
          onChange={(e) => setIntro(e.target.value)}
          rows={10}
          placeholder="What this month was about, and what the reader should take from it."
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="emnote">On the metrics</Label>
        <Textarea
          id="emnote"
          value={metricsNote}
          onChange={(e) => setMetricsNote(e.target.value)}
          rows={5}
          placeholder="What the month's readings say. Rendered under the metrics themselves."
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="eenote">On the assessment</Label>
        <Textarea
          id="eenote"
          value={evaluationNote}
          onChange={(e) => setEvaluationNote(e.target.value)}
          rows={6}
          placeholder="Where the team is strong, where it needs work, and why. Rendered under the scores."
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="econc">Conclusion</Label>
        <Textarea
          id="econc"
          value={conclusion}
          onChange={(e) => setConclusion(e.target.value)}
          rows={8}
          placeholder="Where this leaves the team, and what happens next."
        />
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" onClick={guard.requestClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={busy}>
          Save
        </Button>
      </div>
    </form>
  )
}
